import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2, Save, User } from 'lucide-react';

import { adminApi } from '../../services/api.jsx';
import { annoncerSucces, EtatErreur, Squelette } from '../../etats';

/* ═══════════════════════════════════════════════════════════════════════════
   MON COMPTE

   Cette carte en remplace deux. « Informations de compte » et « Identifiants
   de connexion » demandaient l'une et l'autre le prénom, le nom et l'adresse
   e-mail — dans deux formulaires distincts, avec deux boutons d'envoi, vers
   deux endpoints différents. Qui modifiait son adresse dans l'un et
   enregistrait dans l'autre écrasait sa propre saisie sans le voir.

   Les deux versions n'étaient pas équivalentes, et c'est la seconde qui a été
   gardée :

   - la carte « informations » écrivait sur `/users/manage/<id>/`, réservé aux
     administrateurs. Un analyste ou un coach — qui ont pourtant accès à leurs
     paramètres — n'y recevaient qu'un 403 ;
   - elle ne demandait aucun mot de passe. Or changer son adresse e-mail
     revient à changer l'adresse de récupération du compte : une session
     laissée ouverte sur un poste partagé suffisait à le détourner.

   Ce qui ne se modifie pas — identifiant technique, rôle, statut, dates — est
   conservé, mais rangé à part et en lecture seule. Mêler un champ qu'on
   remplit et un champ qu'on ne peut pas remplir dans la même grille laisse
   croire qu'on peut se promouvoir soi-même.
   ═══════════════════════════════════════════════════════════════════════════ */

const CHAMPS = [
  { cle: 'username', libelle: "Identifiant de connexion", auto: 'username', type: 'text' },
  { cle: 'email', libelle: 'Adresse e-mail', auto: 'email', type: 'email' },
  { cle: 'first_name', libelle: 'Prénoms', auto: 'given-name', type: 'text' },
  { cle: 'last_name', libelle: 'Nom', auto: 'family-name', type: 'text' },
];

const formatDate = (valeur) => {
  if (!valeur) return '—';
  const date = new Date(valeur);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('fr-FR');
};

export default function MesIdentifiants({ onModifie }) {
  const [valeurs, setValeurs] = useState({
    username: '', email: '', first_name: '', last_name: '',
  });
  const [initial, setInitial] = useState(null);
  const [compte, setCompte] = useState(null);
  const [chargement, setChargement] = useState(true);
  // L'objet erreur du chargement : son statut choisit l'écran.
  const [erreurChargement, setErreurChargement] = useState(null);
  const [essai, setEssai] = useState(0);
  const [motDePasse, setMotDePasse] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [retour, setRetour] = useState({ type: '', message: '' });

  useEffect(() => {
    let actif = true;
    setChargement(true);
    setErreurChargement(null);
    adminApi.getMesIdentifiants()
      .then((donnees) => {
        if (!actif) return;
        const lot = {
          username: donnees.username || '', email: donnees.email || '',
          first_name: donnees.first_name || '', last_name: donnees.last_name || '',
        };
        setValeurs(lot);
        setInitial(lot);
        setCompte(donnees);
      })
      .catch((e) => actif && setErreurChargement(e))
      .finally(() => actif && setChargement(false));
    return () => { actif = false; };
  }, [essai]);

  const modifie = initial && CHAMPS.some(({ cle }) => valeurs[cle] !== initial[cle]);
  const adresseChangee = initial && valeurs.email !== initial.email;

  const informations = useMemo(() => {
    const d = compte || {};
    return [
      { cle: 'id', libelle: 'Identifiant technique', valeur: d.id ?? '—' },
      { cle: 'role', libelle: 'Rôle', valeur: d.role || '—' },
      { cle: 'is_active', libelle: 'Statut', valeur: d.is_active === undefined ? '—' : (d.is_active ? 'Actif' : 'Désactivé') },
      { cle: 'date_joined', libelle: 'Compte créé le', valeur: formatDate(d.date_joined) },
      { cle: 'first_login', libelle: 'Profil complété', valeur: d.first_login === undefined ? '—' : (d.first_login ? 'Non' : 'Oui') },
    ];
  }, [compte]);

  const enregistrer = async () => {
    if (!modifie) return;
    if (!motDePasse) {
      setRetour({ type: 'error',
                  message: 'Saisissez votre mot de passe actuel pour confirmer.' });
      return;
    }
    setEnCours(true);
    setRetour({ type: '', message: '' });
    try {
      const reponse = await adminApi.majMesIdentifiants({
        ...valeurs, mot_de_passe: motDePasse,
      });
      const lot = {
        username: reponse.username ?? valeurs.username,
        email: reponse.email ?? valeurs.email,
        first_name: reponse.first_name ?? valeurs.first_name,
        last_name: reponse.last_name ?? valeurs.last_name,
      };
      setInitial(lot);
      setValeurs(lot);
      setCompte((c) => ({ ...(c || {}), ...lot }));
      setMotDePasse('');
      annoncerSucces(reponse.detail || 'Informations mises à jour.');
      onModifie?.(lot);
    } catch (e) {
      setRetour({ type: 'error', message: e.message || "La modification a échoué." });
    } finally {
      setEnCours(false);
    }
  };

  return (
    <section className="rounded-carte shadow-carte bg-[var(--bg-card)] p-6">
      <h3 className="text-[15px] font-semibold text-[var(--text-bold)] flex items-center gap-2">
        <User className="w-4 h-4 text-[var(--accent-texte)]" aria-hidden="true" />
        Mon compte
      </h3>
      <p className="text-[12.5px] text-[var(--text-muted)] mt-1 mb-5 max-w-xl leading-relaxed">
        Vous pouvez vous connecter indifféremment avec votre identifiant ou votre
        adresse e-mail.
      </p>

      {erreurChargement ? (
        <EtatErreur erreur={erreurChargement} onReessayer={() => setEssai((n) => n + 1)} compact />
      ) : chargement ? (
        // La forme du formulaire qui arrive : quatre champs, deux colonnes.
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" role="status" aria-label="Chargement de vos informations">
          {CHAMPS.map((champ) => (
            <div key={champ.cle}>
              <Squelette className="h-3 w-28 mb-2" />
              <Squelette className="h-11 w-full" />
            </div>
          ))}
        </div>
      ) : (
      <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {CHAMPS.map((champ) => (
          <div key={champ.cle}>
            <label htmlFor={`id-${champ.cle}`}
                   className="block text-[13px] font-medium text-[var(--text-main)] mb-1.5">
              {champ.libelle}
            </label>
            <input
              id={`id-${champ.cle}`}
              type={champ.type}
              autoComplete={champ.auto}
              value={valeurs[champ.cle]}
              disabled={chargement}
              onChange={(e) => setValeurs((v) => ({ ...v, [champ.cle]: e.target.value }))}
              style={{ minHeight: 44 }}
              className="champ"
            />
          </div>
        ))}
      </div>

      {modifie && (
        <div className="mt-5 pt-5 border-t border-[var(--border)]">
          <label htmlFor="id-confirmation"
                 className="block text-[13px] font-medium text-[var(--text-main)] mb-1.5">
            Mot de passe actuel
          </label>
          <input
            id="id-confirmation"
            type="password"
            autoComplete="current-password"
            value={motDePasse}
            onChange={(e) => setMotDePasse(e.target.value)}
            placeholder="Pour confirmer que c'est bien vous"
            style={{ minHeight: 44 }}
            className="champ md:max-w-sm"
          />
          {adresseChangee && (
            <p className="text-[12.5px] text-[var(--text-muted)] mt-2 leading-relaxed max-w-xl">
              Votre ancienne adresse sera prévenue de ce changement. C'est le seul
              moyen d'être alerté si quelqu'un tentait d'y substituer la sienne.
            </p>
          )}
        </div>
      )}

      {retour.message && (
        <div role="alert" className="mt-4 flex items-start gap-2 text-[13px] text-[var(--alerte)]">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
          <span className="leading-relaxed">{retour.message}</span>
        </div>
      )}

      <div className="mt-5 flex justify-end">
        <button
          type="button"
          onClick={enregistrer}
          disabled={enCours || !modifie}
          aria-busy={enCours || undefined}
          style={{ minHeight: 44, backgroundColor: 'var(--accent)', color: '#0A1128' }}
          className="act act-tertiaire"
        >
          {enCours ? <Loader2 className="w-4 h-4 animate-spin" />
                   : <Save className="w-4 h-4" />}
          Enregistrer
        </button>
      </div>
      </>
      )}

      {/* ── Ce qui ne se modifie pas ──────────────────────────────────────
          En lecture seule, et sous le bouton d'enregistrement : au-dessus,
          ces champs auraient laissé croire qu'ils font partie de la saisie. */}
      <div className="mt-6 pt-5 border-t border-[var(--border)]">
        <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--text-muted)] mb-4">
          Informations de compte
        </h4>
        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
          {informations.map((item) => (
            <div key={item.cle}>
              <dt className="text-[12px] text-[var(--text-muted)]">{item.libelle}</dt>
              <dd className="text-[13.5px] font-medium text-[var(--text-bold)] mt-0.5 break-words">
                {item.valeur}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}
